import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useRouteMatch, useParams } from "react-router-dom";
import { getBaseUrl } from "services/util";
import _ from "lodash";
import cx from "classnames";
import { toast } from "react-toastify";
import { Container, Row, Col, Button, Image } from "react-bootstrap";
import Truncate from "react-truncate";
import Tooltip from "components/Tooltip";

import PromptInputField from "../../components/promptInputField";
import { CreateStoryboard } from "pages/storyboards/templates";
import { WarningToast } from "components/Toast";
import { promptEditorSectionRequest, resetPromptCreationFlow} from "actions/promptEditor";

import styles from "./creationStep.module.scss";


export default function CreationStep({
  onBack,
  promptCreation,
  updateStep,
}) {
  const dispatch = useDispatch();
  const history = useHistory();
  const { url } = useRouteMatch();
  const baseUrl = getBaseUrl(url);
  const { listId } = useParams();
  
  const userName = useSelector(state =>
    _.get(state, "users.me.firstName", "User")
);
  const [state, setState] = useState({
    create: false,
    delete: null,
  });
  const [tooltip, setTooltip] = useState(false)

  const [modalState, setModalState] = useState({
    showLinkModal: false,
    showFramesModal: false,
    showFontsModal: false,
    showContentTypePanel: false,
  });

  const [pptState, setPptState] = useState({
    showPPTModal: false,
    pptFile: null,
  });

  const [storyboardState, setStoryboardState] = useState({
    content: null,
    storyboardName: "",
  });

  // Local state for prompt input
  const [promptValue, setPromptValue] = useState(
    _.get(promptCreation, "data.prompt", ""),
  );
  const [contentType] = useState(
    _.get(promptCreation, "contentType", "LANDING_PAGE"),
  );
  const workspace = useSelector(state => _.get(state, `workspaces.workspace.item.id`));

  const handleManualCreation = () => {
    setState((state) => {
      return {
        ...state,
        create: true,
      };
    });
  };

  const handlePromptChange = (e) => {
    setPromptValue(e.target.value);
  };

  const handlePromptSubmit = () => {
    if (!promptValue?.trim()) {

      toast(
        <WarningToast
          heading={`Warning`}
          description={`Please enter a prompt to generate a storyboard`}
        />,
        {
          type: toast.TYPE.DEFAULT,
          position: toast.POSITION.BOTTOM_RIGHT,
          autoClose: 3000,
        }
      );
      return;
    }

    // Dispatch action to generate outline
    dispatch(
      promptEditorSectionRequest(
        {prompt: promptValue?.trim()},
        workspace,
      ),
    );

    // Move to step 2 (loader)
    updateStep(2);
  };

  const closePopups = () => {
    setState((state) => {
      return {
        ...state,
        create: false,
        delete: null
      };
    });
    const url = listId
      ? `${baseUrl}/storyboards/folder/${listId}`
      : `${baseUrl}/storyboards`;
    history.push(url);
  };

  const updateProperty = (fieldName, value) => {
    setModalState((state) => {
      return {
        ...state,
        [fieldName]: value,
      };
    });
  };

  const handleTooltip = (truncated) => {
    setTooltip(truncated);
  }

  return (
    <>
      <Container className={styles.creationStep} fluid>
        <Row className={styles.creationStepRow}>
          <Col md={6} className={styles.uploadContent}>
            <div className={styles.leftColumn}>
              <div className={styles.headerContainer}>
                <div className={styles.backButtonContainer}>
                  <Button
                    variant="info"
                    onClick={()=>{
                      onBack();
                      dispatch(resetPromptCreationFlow());
                    }}
                    className={styles.backButton}
                  >
                    <i className="cleverstory-drop-down-left"></i>
                  </Button>
                  <hr className={styles.separator} />
                  <i
                    className={cx(
                      styles.logoSmallIcon,
                      "cleverstory-logo-cs-small",
                    )}
                  ></i>
                </div>
              </div>
              <div className={styles.creationContainer}>
                <CreateStoryDesign
                  handleManualCreation={handleManualCreation}
                />
              </div>
            </div>
          </Col>
          <Col md={6} className={styles.chatContainer}>
            <div className={styles.rightColumn}>
              <div className={styles.promptBaseCard}>
                <div className={styles.promptHeader}>
                  <div className={styles.greeting}>
                    <div className={styles.sparkleIcon} />
                      <Tooltip placement="top" disabled={!tooltip} content={`Hello! ${userName}`} >
                        <span className={styles.greetingText}>
                          <Truncate onTruncate={handleTooltip} width={500} lines={1}>
                            {`Hello! ${userName}`}                        
                          </Truncate>
                        </span>
                      </Tooltip>
                  </div>
                  <h2 className={styles.promptTitle}>Where should we start?</h2>
                </div>
                <PromptInputField
                  value={promptValue}
                  onChange={handlePromptChange}
                  onSubmit={handlePromptSubmit}
                />
              </div>
            </div>
          </Col>
        </Row>
      </Container>

      {state.create && (
        <CreateStoryboard
          show={state.create}
          onClose={closePopups}
          updateProperty={updateProperty}
          setPptState={setPptState}
          setCreateState={setState}
          storyboardState={storyboardState}
          setStoryboardState={setStoryboardState}
        />
      )}
    </>
  );
}

function CreateStoryDesign({ handleManualCreation }) {
  return (
    <div className={styles.createStoryDesign}>
      <div className={styles.heading}>
        <p className={styles.headingText}>Weather forecast</p>
        <span className={styles.headingSubText}>
          Start with AI or build manually.
        </span>
      </div>
      <div className={styles.imageContainer}>
        <Image
          src={"/images/weatherForecast.svg"}
          fetchPriority="high"
          className={styles.weatherForecastImage}
        />
        <Button
          variant="primary"
          className={styles.manualCreationButton}
          onClick={handleManualCreation}
        >
          <i className="cleverstory-plus"></i>
          Create Storyboard Manually
        </Button>
      </div>
    </div>
  );
}
